import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, CheckCircle2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import Button from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { useAuth } from '../context/AuthContext';
import authService, { type InvitationInfo } from '../services/auth';

/**
 * Where an invitation lands when the invited address already has an account.
 *
 * Nobody is added to an organisation on an admin's word alone: joining shows
 * the person to that organisation and puts them under its admins. So the
 * person accepts here, signed in as the address the invitation went to.
 */
function JoinOrganisation() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, refreshUser, switchOrg, logout } = useAuth();

  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    authService.getInvitation(token)
      .then(info => { if (!cancelled) setInvitation(info); })
      .catch((err: Error) => { if (!cancelled) setLoadError(err.message || 'This invitation is not valid'); });
    return () => { cancelled = true; };
  }, [token]);

  const wrongAccount =
    !!invitation && !!user && invitation.invitedEmail.toLowerCase() !== user.email.toLowerCase();

  const accept = async () => {
    try {
      setBusy(true);
      setError(null);
      const result = await authService.acceptInvitation(token);
      setJoined(result.organisation);
      // The org list in the header is read from the user; pick up the new one.
      await refreshUser().catch(() => {});
    } catch (err) {
      setError((err as Error).message || 'Could not accept the invitation');
    } finally {
      setBusy(false);
    }
  };

  const switchToJoined = async () => {
    if (!joined) return;
    try {
      setBusy(true);
      await switchOrg(joined.id);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Could not switch organisation');
      setBusy(false);
    }
  };

  const signOut = async () => {
    await logout();
    // Straight back here once they have signed in as the right person.
    navigate('/login', { replace: true, state: { from: { pathname: `/join-organisation/${token}` } } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Join an organisation"
        subtitle={invitation ? invitation.organisationName : undefined}
        icon={<Building2 className="h-6 w-6 text-white" />}
      />

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8">
        <Card>
          <CardContent className="p-6 space-y-4">
            {loadError && (
              <>
                <p className="text-neutral-900 font-medium">This invitation cannot be used.</p>
                <p className="text-sm text-neutral-600">
                  {loadError}. Ask whoever invited you to send a new one.
                </p>
                <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
              </>
            )}

            {!loadError && !invitation && (
              <p className="text-neutral-600">Checking your invitation…</p>
            )}

            {invitation && joined && (
              <>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-neutral-900 font-medium break-words">You have joined {joined.name}.</p>
                    <p className="text-sm text-neutral-600 mt-1">
                      Switch between your organisations at any time from the organisation menu. An
                      admin there still needs to give you a seat for each module you will use.
                    </p>
                  </div>
                </div>
                {error && <p className="text-sm text-red-700">{error}</p>}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={switchToJoined} disabled={busy}>
                    {busy ? 'Switching…' : 'Switch to it now'}
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/dashboard', { replace: true })} disabled={busy}>
                    Stay where I am
                  </Button>
                </div>
              </>
            )}

            {invitation && !joined && (
              <>
                <p className="text-neutral-900 break-words">
                  <strong>{invitation.invitedBy}</strong> has invited you to join{' '}
                  <strong>{invitation.organisationName}</strong>.
                </p>

                {wrongAccount ? (
                  <>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 break-words">
                      This invitation was sent to <strong>{invitation.invitedEmail}</strong>, and you are
                      signed in as <strong>{user?.email}</strong>. Sign in with the account it was sent to
                      and open the link again.
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={signOut}>Sign out</Button>
                      <Button variant="outline" onClick={() => navigate('/dashboard')}>Not now</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600 break-words">
                      You keep your account and any organisation you already belong to. Joining lets{' '}
                      {invitation.organisationName}'s admins see your name and email, give you access to
                      their modules, and manage you as one of their users. They cannot affect your access
                      to any other organisation.
                    </p>
                    {error && (
                      <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 break-words">
                        {error}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <Button onClick={accept} disabled={busy}>
                        {busy ? 'Joining…' : `Join ${invitation.organisationName}`}
                      </Button>
                      <Button variant="outline" onClick={() => navigate('/dashboard')} disabled={busy}>
                        Not now
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default JoinOrganisation;
